import styles from "./favorite-button.module.css";
import { Button } from "@wags/ui/button.tsx";
import { toast } from "@wags/ui/sonner.tsx";
import { cn } from "@wags/ui/ui-utils";
import { usePostHog } from "posthog-js/react";
import { useState } from "react";
import {
    didSaveListing,
    type FavoriteMutationResult,
    markSignInPromptShown,
    useFavoriteMutation,
    willStartSave,
} from "./use-favorite-mutation";
import { FavoriteButtonFallback } from "./favorite-button-fallback";
import { HeartIconSwap } from "./heart-icon-swap";
import { useHeartSpill } from "./use-heart-spill";
import { UNDO_WINDOW_MS } from "./saved-listings-collection";
import { iconButtonClass } from "@wags/ui/icon-button-class.ts";
import { useIsMobileViewport, useMediaQuery } from "@wags/tanstack-start/react";
import { useAuth } from "@/lib/core/auth-protocol";
import { useFavoriteInteractions } from "./favorite-interactions-provider";

type FavoriteButtonProps = {
    listingId: string;
    variant?: "overlay" | "icon";
    showUndoToast?: boolean;
    onSaveStart?: () => void;
    onSaved?: (result: FavoriteMutationResult) => void;
    onUnsaved?: () => void;
    className?: string;
};

const TOAST_SHELL_CLASS = cn(
    styles.toast,
    "rounded-nested border border-doberman-black/[0.06] bg-true-white",
);

function useFavoriteOutcomeFeedback({
    listingId,
    variant,
}: {
    listingId: string;
    variant: "overlay" | "icon";
}) {
    const { isChatOpen } = useFavoriteInteractions();
    const [showSignInPrompt, setShowSignInPrompt] = useState(false);
    const [showCapPrompt, setShowCapPrompt] = useState(false);
    const prefersReducedMotion = useMediaQuery(
        "(prefers-reduced-motion: reduce)",
    );
    const [saveMotionKey, setSaveMotionKey] = useState(0);
    const [shakeKey, setShakeKey] = useState(0);
    const isMobile = useIsMobileViewport();
    const posthog = usePostHog();

    const triggerToggleMotion = () => {
        setSaveMotionKey((currentKey) => currentKey + 1);
    };

    const showOutcomeFeedback = (
        result: FavoriteMutationResult | undefined,
    ) => {
        const pillOpen = isChatOpen();

        if (result?.outcome === "save-limit") {
            if (!prefersReducedMotion) {
                setShakeKey((currentKey) => currentKey + 1);
            }
            if (pillOpen) return;

            posthog.capture("save_prompt_shown", {
                listingId,
                prompt: "save_limit",
                surface: `favorite_${variant}`,
            });
            if (isMobile) {
                setShowCapPrompt(true);
                return;
            }
            toast.custom(
                (id) => (
                    <SaveSignInToast
                        title="You've hit your save limit"
                        description="Sign in to save unlimited dogs and keep them across all your devices."
                        listingId={listingId}
                        onDismiss={() => toast.dismiss(id)}
                        prompt="save_limit"
                    />
                ),
                { duration: Infinity, unstyled: true },
            );
            return;
        }

        if (result?.outcome === "guest-saved-sign-in-prompt" && !pillOpen) {
            markSignInPromptShown();
            posthog.capture("save_prompt_shown", {
                listingId,
                prompt: "save_prompt",
                surface: `favorite_${variant}`,
            });
            if (isMobile) {
                setTimeout(() => setShowSignInPrompt(true), 600);
                return;
            }
            setTimeout(
                () =>
                    toast.custom(
                        (id) => (
                            <SaveSignInToast
                                title="Keep your saved dogs"
                                description="Sign in to access them on any device and never lose your favorites."
                                listingId={listingId}
                                onDismiss={() => toast.dismiss(id)}
                                prompt="save_prompt"
                            />
                        ),
                        { duration: 8000, unstyled: true },
                    ),
                600,
            );
            return;
        }
    };

    return {
        prefersReducedMotion,
        saveMotionKey,
        shakeKey,
        showCapPrompt,
        showOutcomeFeedback,
        showSignInPrompt,
        setShowCapPrompt,
        setShowSignInPrompt,
        triggerToggleMotion,
    };
}

export function FavoriteButton({
    listingId,
    variant = "overlay",
    showUndoToast = false,
    onSaveStart,
    onSaved,
    onUnsaved,
    className,
}: FavoriteButtonProps) {
    const { isLoaded, isSignedIn } = useAuth();
    const {
        atSaveLimit,
        isFavorited,
        isPending,
        isReady,
        retryLoad,
        toggle,
        cancelUnsave,
    } = useFavoriteMutation({
        listingId,
        isSignedIn: isLoaded ? isSignedIn : undefined,
    });
    const posthog = usePostHog();
    const {
        SignInPromptSheet,
        captureAuthCtaClicked,
        onSaved: onAuthSaved,
    } = useFavoriteInteractions();
    const feedback = useFavoriteOutcomeFeedback({
        listingId,
        variant,
    });
    const spill = useHeartSpill();
    const { prefersReducedMotion } = feedback;

    if (!isReady) {
        return (
            <FavoriteButtonFallback
                variant={variant}
                className={className}
                onRetry={retryLoad}
            />
        );
    }

    const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();

        if (showUndoToast && isFavorited === true && !isPending) {
            toast.custom(
                (id) => (
                    <UndoUnsaveToast
                        onUndo={() => {
                            cancelUnsave();
                            toast.dismiss(id);
                        }}
                    />
                ),
                {
                    id: `undo-unsave-${listingId}`,
                    duration: UNDO_WINDOW_MS,
                    unstyled: true,
                    className: "flex justify-center",
                },
            );
        } else {
            toast.dismiss(`undo-unsave-${listingId}`);
        }

        if (willStartSave({ isFavorited, atSaveLimit })) onSaveStart?.();

        if (!prefersReducedMotion && !atSaveLimit) {
            feedback.triggerToggleMotion();
            spill.onToggle({
                button: e.currentTarget,
                listingId,
                saved: isFavorited !== true,
            });
        }

        const result = await toggle();
        const savedNow = didSaveListing(result);

        if (savedNow) {
            onAuthSaved();
            onSaved?.(result);
        }
        if (result?.outcome === "unsaved") onUnsaved?.();

        feedback.showOutcomeFeedback(result);
    };

    const iconSwap = (
        <span
            key={`${feedback.saveMotionKey}-${feedback.shakeKey}`}
            data-save-heart-motion
            className={cn(
                "grid shrink-0 place-items-center",
                feedback.saveMotionKey > 0 && "dog-save-heart-pop",
                feedback.shakeKey > 0 && "dog-save-heart-shake",
            )}
        >
            <HeartIconSwap
                className={
                    variant === "overlay" ? "size-6 text-true-white" : "size-6"
                }
                isFavorited={isFavorited === true}
                haloKey={variant === "overlay" ? feedback.saveMotionKey : 0}
                filledClassName={cn(
                    "size-full",
                    variant === "icon" && "text-irish-setter",
                )}
                outlineClassName="size-full"
            />
        </span>
    );

    const promptSheet = (
        <>
            {feedback.showSignInPrompt && (
                <SignInPromptSheet
                    title="Keep your saved dogs"
                    description="Sign in to access them on any device and get notified about availability."
                    open={feedback.showSignInPrompt}
                    onOpenChange={feedback.setShowSignInPrompt}
                    onSignIn={() =>
                        captureAuthCtaClicked(posthog, {
                            listingId,
                            prompt: "save_prompt",
                            surface: "favorite_sheet",
                        })
                    }
                />
            )}
            {feedback.showCapPrompt && (
                <SignInPromptSheet
                    title="You've hit your save limit"
                    description="Sign in to save unlimited dogs and keep them across all your devices."
                    open={feedback.showCapPrompt}
                    onOpenChange={feedback.setShowCapPrompt}
                    onSignIn={() =>
                        captureAuthCtaClicked(posthog, {
                            listingId,
                            prompt: "save_limit",
                            surface: "favorite_sheet",
                        })
                    }
                />
            )}
        </>
    );

    return (
        <>
            <button
                type="button"
                data-save-button
                aria-label={isFavorited ? "Remove from saved" : "Save this dog"}
                aria-pressed={isFavorited === true}
                disabled={!isLoaded}
                onClick={handleClick}
                className={cn(
                    variant === "icon"
                        ? cn(iconButtonClass, "active:scale-100")
                        : cn(
                              styles.overlay,
                              "absolute top-2.5 right-2.5 z-10 flex size-10 items-center justify-center rounded-full backdrop-blur-sm after:absolute after:-inset-0.5",
                              isFavorited
                                  ? "bg-irish-setter [@media(hover:hover)]:hover:bg-irish-setter/90"
                                  : "bg-doberman-black/60 [@media(hover:hover)]:hover:bg-doberman-black/70",
                          ),
                    "transition-colors",
                    className,
                )}
            >
                {iconSwap}
            </button>
            {spill.hearts}
            {promptSheet}
        </>
    );
}

function UndoUnsaveToast({ onUndo }: { onUndo: () => void }) {
    return (
        <div
            className={cn(
                TOAST_SHELL_CLASS,
                "flex items-center gap-4 px-5 py-3.5",
            )}
        >
            <p className="text-body text-doberman-black">Removed from saved</p>
            <button
                type="button"
                onClick={onUndo}
                className="-my-3 -mx-2 inline-flex min-h-11 min-w-11 items-center justify-center rounded-nested px-2 text-label text-doberman-black shrink-0 transition-colors hover:text-doberman-black/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-labrador-yellow"
            >
                Undo
            </button>
        </div>
    );
}

function SaveSignInToast({
    title,
    description,
    listingId,
    onDismiss,
    prompt,
}: {
    title: string;
    description: string;
    listingId: string;
    onDismiss: () => void;
    prompt: string;
}) {
    const posthog = usePostHog();
    const { ResponsiveSignInButton, captureAuthCtaClicked } =
        useFavoriteInteractions();

    return (
        <div className={cn(TOAST_SHELL_CLASS, "w-95 px-6 py-5")}>
            <p className="text-body font-medium text-doberman-black">{title}</p>
            <p className="mt-1.5 text-body-small text-doberman-black/60">
                {description}
            </p>
            <div className="mt-4 flex items-center justify-end gap-3">
                <button
                    type="button"
                    onClick={onDismiss}
                    className="text-body-small font-medium text-doberman-black/50 transition-colors hover:text-doberman-black"
                >
                    Not now
                </button>
                <ResponsiveSignInButton>
                    <Button
                        onClick={() =>
                            captureAuthCtaClicked(posthog, {
                                listingId,
                                prompt,
                                surface: "favorite_toast",
                            })
                        }
                    >
                        Sign in
                    </Button>
                </ResponsiveSignInButton>
            </div>
        </div>
    );
}
